import React, { useCallback, useState, useEffect } from "react";
import { Form, InputProps, Typography } from "antd";
import { DefaultInput } from "ui";
import { _isRequired, _isValidUnkFormat, unkFormatRegex } from "utils";
import { getBankByUnk } from "services";
import { UnkValidationResponse, USER_SCOPE_ENUM } from "interfaces";
import { useStores } from "stores";

interface IProps {
  inputProps: InputProps;
  onUnkDataReceived?: (bankData: UnkValidationResponse) => void;
  showHints?: boolean;
}

export const UnkInput = (props: IProps) => {
  const { user } = useStores();
  const form = Form.useFormInstance();

  const unkValue: string = Form.useWatch("unk", form);

  const [unkData, setUnkData] = useState<UnkValidationResponse | null>(null);
  const [lastFetchedUnk, setLastFetchedUnk] = useState<string | null>(null);

  const isCustomer = [USER_SCOPE_ENUM.JUNIOR_CUSTOMER, USER_SCOPE_ENUM.SENIOR_CUSTOMER].some((role) =>
    user.scope.includes(role)
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valueWithoutSpaces = e.target.value.replace(/\s/g, "");
    form.setFieldValue("unk", valueWithoutSpaces);

    if (!valueWithoutSpaces) {
      setUnkData(null);
      setLastFetchedUnk(null);
    }
  };

  const fetchBankData = useCallback(
    async (unkValue: string) => {
      if (!unkValue) {
        setUnkData(null);
        setLastFetchedUnk(null);
        return;
      }

      const cleanValue = unkValue.replace(/\s/g, "");

      if (!unkFormatRegex.test(cleanValue)) {
        setUnkData(null);
        setLastFetchedUnk(null);
        return;
      }

      if (lastFetchedUnk === cleanValue) {
        return;
      }

      setLastFetchedUnk(cleanValue);

      await getBankByUnk({ unk: cleanValue })
        .then((bankData) => {
          setUnkData(bankData);
          if (bankData && props.onUnkDataReceived) {
            props.onUnkDataReceived(bankData);
          }
        })
        .catch((e) => {
          console.error(e);
          setUnkData(null);
        });
    },
    [props, lastFetchedUnk]
  );

  useEffect(() => {
    if (unkValue) {
      fetchBankData(unkValue);
    }
  }, [unkValue, fetchBankData]);

  const handleBlur = () => {
    fetchBankData(unkValue);
  };

  const getBankHelp = () => {
    if (!unkData || isCustomer) return undefined;

    return unkData.bankName ? `Уполномоченный банк: ${unkData.bankName}` : "Договор зарегистрирован в другом банке";
  };

  return (
    <Form.Item
      name="unk"
      rules={[_isRequired("Введите номер договора в формате 12345678/1234/1234/1/1"), _isValidUnkFormat()]}
      validateTrigger="onBlur"
      help={getBankHelp()}
    >
      <DefaultInput
        {...props.inputProps}
        value={unkValue}
        placeholder="№ Договора (УНК)"
        disabled={props.inputProps.disabled}
        onChange={handleChange}
        onBlur={handleBlur}
      />
      {props.showHints && (
        <Typography.Text className="text-hint">В формате 12345678/1234/1234/1/1</Typography.Text>
      )}
    </Form.Item>
  );
};
